import styled, { createGlobalStyle } from 'styled-components';

// The wireframe's Draft Feedback bar (.cmp-bar): its note, then Submit comments at the right, as a padded bar with room
// around it (not squeezed against the compare panes)
export const FeedbackBarStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 0 12px;
  padding: 10px 16px;
  border: 1px solid #e3e7ee;
  border-radius: 8px;
  background: #f7f9fc;
`;

export const FeedbackNoteStyled = styled.span`
  flex: 1;
  min-width: 0;
  color: #5b6474;
  font-size: 13px;
  line-height: 1.5;
`;

// The stacked comments under a changed sentence (.fbc), a reply indented, a not-submitted one dashed
export const ThreadsStyled = styled.div`
  margin: 2px 0 10px 12px;
`;

export const CommentMetaStyled = styled.div`
  color: #5b6474;
  margin-bottom: 2px;
`;

// Resolve on the owner's comment cards: a real small button (border and fill) that darkens on hover; disabled while any
// resolve is in flight
export const ResolveButtonStyled = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 1px 8px;
  border: 1px solid #c5d3ee;
  border-radius: 4px;
  background: #eef3fd;
  color: #1f5fd1;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    background: #d6e2fb;
    border-color: #1f5fd1;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

// ── The shared person's threads (Draft Feedback), collapsed behind a comment button ─────────────────────────────

// The comment button at a changed sentence's top right, the only way into its comments: a real button in the app's
// primary blue (stands out on the yellow highlight, matches the other blue actions) that darkens on hover; the count,
// and an orange dot when one of its comments is not sent yet
export const CommentBadgeStyled = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px;
  border: 1px solid #0155e7;
  border-radius: 12px;
  background: #ffffff;
  color: #0155e7;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 85, 231, 0.15);
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: #e6efff;
    color: #0042b5;
  }

  &[aria-expanded='true'] {
    background: #0155e7;
    color: #ffffff;
  }
`;

export const UnsentDotStyled = styled.span`
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border: 1px solid #ffffff;
  border-radius: 50%;
  background: #fa8c16;
`;

// One thread, lighter than a boxed card: a thin left rule; replies indented under a fainter rule
export const ThreadCardStyled = styled.div`
  margin: 6px 0 0 ${({ $reply }) => ($reply ? '16px' : '0')};
  padding: 4px 10px;
  border-left: 2px solid ${({ $reply, $pending }) => ($pending ? '#fa8c16' : $reply ? '#e3e7ee' : '#c5ccd8')};
  font-size: 13px;

  /* the owner's editor: one thin line (who · when, the comment, the actions at the right) */
  ${({ $compact }) => ($compact ? `
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-top: 2px;
    padding: 1px 8px;
    line-height: 1.4;
  ` : '')}
`;

// the compact card's "who · when" and its text between that and the actions
export const ThreadMetaStyled = styled.span`
  flex-shrink: 0;
  color: #5b6474;
  white-space: nowrap;
`;

export const ThreadTextStyled = styled.span`
  flex: 1;
  min-width: 0;
`;

export const ThreadHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #5b6474;
`;

export const ThreadActionsStyled = styled.span`
  display: inline-flex;
  gap: 2px;
  flex-shrink: 0;
`;

// Reply / Edit / Delete: small grey icon buttons that darken on hover
export const IconActionStyled = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #8790a4;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    background: #eef1f6;
    color: #1f2433;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const NotSentTagStyled = styled.span`
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 8px;
  background: #fff4e5;
  color: #ad6800;
  font-size: 11px;
  line-height: 18px;
`;

// The add / reply / edit box, styled as the comment it becomes: the thread card's thin left rule (orange, like a comment
// not sent yet), a "You" header, a text box that grows as you type, and small Cancel / confirm at the bottom right
export const CommentBoxStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 320px;
  margin: 6px 0 0 ${({ $reply }) => ($reply ? '16px' : '0')};
  padding: 4px 0 4px 10px;
  border-left: 2px solid #fa8c16;
  font-size: 13px;

  textarea.ant-input {
    font-size: 13px;
    border-radius: 6px;
  }
`;

export const CommentBoxActionsStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

// The owner's editor: the comments panel above the editor (.ecmt), and the orphan list (.ecmt.orphan)
export const CommentsPanelStyled = styled.div`
  margin: 0 0 8px;
  padding: 8px 12px;
  border: 1px solid #ffd88a;
  border-radius: 6px;
  background: #fffaf1;
  font-size: 13px;
`;

export const CommentsGroupStyled = styled.div`
  margin-top: 6px;

  > q {
    display: block;
    color: #5b6474;
    font-style: italic;
  }
`;

// The commented sentences in the editor (the wireframe's .rb.hl), marked through the CSS Custom Highlight API
export const CommentHighlightStyle = createGlobalStyle`
  ::highlight(jd-draft-comment) {
    background-color: #fff3c4;
  }
`;

// A commented sentence's cards, placed right above it in the editor (the wireframe's .ecmt over its .rb): positioned
// in the editor's scroll area, over the space the sentence's block leaves above itself (DraftCommentsPanel)
export const AnchoredLayerStyled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 2;
`;

// A commented line's comments in the owner's editor, right-aligned above the line: folded, only the 💬 button (no frame);
// open, the button then the thread in the panel's frame
export const AnchoredCardsStyled = styled(CommentsPanelStyled)`
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 2px 6px 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  ${({ $collapsed }) => ($collapsed ? `
    padding: 0;
    border-color: transparent;
    background: transparent;
    box-shadow: none;
  ` : '')}
`;

// The 💬 button at the right of the line's slot
export const AnchoredToggleRowStyled = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const AnchoredCardsListStyled = styled.div`
  min-width: 0;
`;
